import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import {
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
} from '@pelicargo/datatable';
import { useCrud } from '@pelicargo/datatable';
import { createPrismaQuery, createPrismaSort } from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { RuleType } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { startCase } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { RuleDataDrawer } from '../components/Drawers/RuleDataDrawer';
import { trpc, TrpcOutputs } from '../config/trpc';

type TableColumns = {
  id: number;
  provider_id: string;
  account: TrpcOutputs['getRule']['account'];
  type: string;
  action?: any;
};

export const ruleFilters = [
  {
    name: 'provider_id',
    label: 'Airline ID',
    type: 'string',
    inputType: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  {
    name: 'account.name',
    label: 'Account Name',
    type: 'string',
    inputType: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },

  {
    name: 'type',
    label: 'Rule Type',
    type: 'string',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: Object.keys(RuleType).map((name) => ({
      label: startCase(name),
      value: name,
    })),
  },
] satisfies DataTableFilter[];

export const RulesScreen = () => {
  const ruleDrawer = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud();

  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);

  const { data, refetch } = trpc.searchRules.useQuery({
    skip,
    take,
    search: query,
    orderBy: createPrismaSort(sorting),
    filters: createPrismaQuery(filters) as any,
  });
  const count = data?.[0];

  // Todo at some point
  const { mutateAsync: deleteRule } = trpc.deleteRule.useMutation();

  const handleAddFilter = (filter: DataTableFilter) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedRuleId(row?.id);
      ruleDrawer.onOpen();
    },
    [ruleDrawer],
  );

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        size: Number.MIN_SAFE_INTEGER,
      }),
      columnHelper.accessor('provider_id', {
        header: 'Airline',
      }),
      columnHelper.accessor('account', {
        header: 'Account',
        cell: (cell) => {
          return cell.row.original?.account?.['name'] ?? '-';
        },
      }),
      columnHelper.accessor('type', {
        header: 'Rule Type',
      }),
      columnHelper.accessor('action', {
        header: 'Delete',
        cell: (cell) => {
          const row = cell.row.original;

          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem
                    onClick={async () => {
                      await deleteRule({
                        id: row.id,
                      });
                      refetch();
                    }}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, deleteRule, refetch],
  );

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Rules (MTIP)</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
            <DataTableAddFilter
              activeFilters={filters?.map((filter) => filter.name)}
              options={ruleFilters}
              handleAddFilter={handleAddFilter}
            />
          </Stack>
          <Button
            colorScheme="brand"
            onClick={() => {
              setSelectedRuleId(undefined);
              ruleDrawer.onOpen();
            }}
          >
            Create Rule
          </Button>
        </Stack>
        <Show if={filters.length > 0}>
          <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
        </Show>
      </Stack>
      <DataTable
        columns={columns as any}
        data={data?.[1] ?? []}
        dataCount={count ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <RuleDataDrawer
        isOpen={ruleDrawer.isOpen}
        onClose={ruleDrawer.onClose}
        ruleId={selectedRuleId}
        refetch={refetch}
      />
    </Stack>
  );
};
